import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import Layout from "../../Layout/Layout";
import { isAuthenticated } from "../../auth";
import { elakayAutoText as text } from "../../pageInside/dataElakay";
import { recordProviderView } from "../services/providerProductService";
import useProviderStorefront from "./useProviderStorefront";
import { buildProviderShopAdapter, ProviderShopAbout, ProviderShopHeader, ProviderShopHome, publicShopPaths } from "./ProviderShop";
import "../seller/DropshippingIn.css";

// /provider-shop/:providerId[?page=<pageId>] and /provider-shop/:providerId/about -- the Provider Shop
// website (Seller shop architecture: shop header, identity, hero, Shop Categories, products, footer).
// Published pages only, no owner/builder controls. Reached by Sellers and the other professional
// roles (route guard + professional-only API), and by the Provider on its own shop.
export default function ProviderStorefrontPage({ infoSource }) {
  const { providerId } = useParams();
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const pageId = params.get("page");
  // Multi-shop: /provider-shop/:providerId?shop=<shopId> = one of the Provider's shops.
  const shopParam = params.get("shop") || null;
  const section = /\/about\/?$/.test(pathname) ? "about" : "home";
  const token = isAuthenticated()?.token;
  const state = useProviderStorefront(providerId, shopParam);
  const [refreshKey, setRefreshKey] = useState(0);
  const { reload } = state;
  const refresh = useCallback(() => { reload(); setRefreshKey((k) => k + 1); }, [reload]);

  // One Provider view per real shop visit (keyed to the Provider, never a re-render, a page switch
  // inside the shop or the About page); a Provider looking at its own shop is not a visit.
  const viewerId = infoSource?.userInfo?._id || isAuthenticated()?.user?._id;
  useEffect(() => {
    if (token && providerId && String(viewerId || "") !== String(providerId)) recordProviderView(token, providerId);
  }, [token, providerId, viewerId]);

  // Links use the id from the URL (a publicId keeps working) until the storefront answered.
  const shop = useMemo(() => buildProviderShopAdapter(state.storefront || { provider: { _id: providerId } }), [state.storefront, providerId]);
  const paths = useMemo(() => publicShopPaths(providerId, shopParam), [providerId, shopParam]);
  const header = (drawerClickHander) => (
    <ProviderShopHeader infoSource={infoSource} shop={shop} paths={paths} section={section} activePageId={pageId} drawerClickHander={drawerClickHander} onRefresh={refresh} />
  );
  useEffect(() => { if (typeof window !== "undefined") window.scrollTo({ top: 0 }); }, [section, pageId]);

  const common = { summary: state.storefront, previewProducts: state.products, categories: state.categories, paths, mode: "public", infoSource, token };
  return (
    <Layout {...infoSource?.allSettings?.layout} infoSource={infoSource} titleElakay="providerProducerIn" SliderPunShow={false} loading={false}
      outOffPage={true} allHeaderShop={header} inpage="providerShop" showFooter={true}>
      <div className="psh-page" data-shop-type="provider">
        {state.loading && !state.storefront && <div className="dsi-skeleton" role="status" aria-busy="true"><div className="dsi-skeleton__shop" /></div>}
        {!state.loading && state.error && (
          <div className="dsi-state-card" role="alert">
            <h2>{state.status === 404 ? text("h_psf_not_found", "This Provider storefront does not exist.") : text("h_spm_error_title", "Something went wrong")}</h2>
            <p>{state.error}</p>
            <Link className="dsi-state-card__btn" to="/seller/providers">{text("h_spm_title", "Providers & Producers")}</Link>
          </div>
        )}
        {state.storefront && (section === "about"
          ? <ProviderShopAbout {...common} custom={state.storefront.custom} />
          : <ProviderShopHome {...common} custom={undefined} pageId={pageId} refreshKey={refreshKey} productsLoading={state.loading} />)}
      </div>
    </Layout>
  );
}
